import React from 'react';

import { IApiResult, IUserData, IUserFetchResult } from '../model/api';
import { useStateWithStorage } from '../utils/storage';
import { ApiContext } from './apicontext';

export enum SessionInitState {
	Uninitialized,
	LoadedFromStorage,
	FetchingProfile,
	Ready
}

export interface ISessionContext {
	initState: SessionInitState;
	session: string;
	setSession: (session: string) => void;
	user: IUserData | undefined;
	setUser: (user: IUserData | undefined) => void;
}

const defaultSession: ISessionContext = {
	initState: SessionInitState.Uninitialized,
	session: '',
	setSession: () => {},
	user: undefined,
	setUser: () => {}
};

export const SessionContext = React.createContext<ISessionContext>(defaultSession);

type SessionProviderProps = {
	children: React.ReactNode;
};

export const SessionProvider = (props: SessionProviderProps) => {
	const Api = React.useContext(ApiContext);
	const { children } = props;

	const [initState, setInitState] = React.useState<SessionInitState>(SessionInitState.Uninitialized);
	const [session, setSession] = useStateWithStorage<string>(
		'session',
		'',
		{
			rememberForever: true,
			onInitialize: () => setInitState(SessionInitState.LoadedFromStorage)
		}
	);
	const [user, setUser] = useStateWithStorage<IUserData | undefined>('user', undefined);

	React.useEffect(() => {
		// Listen for session changes from other tabs
		window.addEventListener('storage', handleStorageEvent);
		// Cleanup
		return () => {
			window.removeEventListener('storage', handleStorageEvent);
		};

		function handleStorageEvent(event: StorageEvent): void {
			if (event.key === 'session') {
				// Log out
				if (event.oldValue !== '' && !event.newValue) {
					setSession('');
				}
				// Session changed (e.g. log in)
				else if (event.newValue) {
					setSession(JSON.parse(event.newValue));
				}
			}
		}
	}, [setSession]);

	React.useEffect(() => {
		if (initState < SessionInitState.LoadedFromStorage) return;
		if (session !== '' && !user) {
			const headers = {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${session}`
			};
			Api.fetch('user/public',
				{ headers },
				(result: IApiResult) => {
					if (result.response === 1)
						setUser((result as IUserFetchResult).user);
					// Response == 0: No valid session match => Clear session
					else if (result.response === 0)
						setSession('');
					// Response == -1: API unavailable. Do not clear session
					setInitState(SessionInitState.Ready);
				}
			);
			setInitState(SessionInitState.FetchingProfile);
		}
		// Clear user if at this point there's somehow a user without a session
		else if (session === '' && user) {
			setUser(undefined);
			setInitState(SessionInitState.Ready);
		}
	}, [initState, session]);

	const providerValue: ISessionContext = {
		initState,
		session,
		setSession,
		user,
		setUser
	};

	return (
		<SessionContext.Provider value={providerValue}>
			{children}
		</SessionContext.Provider>
	);
};
